"use client";

import { useEffect, useState } from "react";

import { OAuthButton } from "@/components/forms/oauth-button";
import {
  preloadNativeSignIn,
  resolveShellSignIn,
  runNativeSignIn,
} from "@/lib/auth/native-shell-sign-in";
import { withQuery } from "@/lib/auth/error-return";
import { startShellOAuth } from "@/lib/auth/oauth";
import type { OAuthFormAction, OAuthProvider } from "@/lib/auth/oauth-providers";
import { useShellFlag } from "@/lib/shell-client";

/** A provider sign-in form, shared by /login and /signup. In browsers it
 *  posts the redirect-based server action unchanged. In the native shell it
 *  intercepts the submit and prefers the provider's NATIVE sheet when the
 *  registry has a runner for it (Apple and Google) — in-app, no browser hop.
 *
 *  Everything below is the REDIRECT path, taken by a provider with no
 *  native runner and as the fallback when one reports `unavailable`: fetch
 *  the authorize URL and main-frame
 *  `location.assign` it. Capacitor policy-cancels the off-host hop-0
 *  navigation into the system browser while the webview stays parked with
 *  ShellBridge armed for the deep-link return. Deliberately NOT
 *  `window.open` — after the async round-trip the tap's transient
 *  activation has lapsed and WKWebView (popups disabled by default) would
 *  silently return null on slow connections; a main-frame navigation has
 *  no activation requirement, and a hop-0 policy-cancel doesn't fire the
 *  errorPath handler (unlike the redirect-hop cancel that ruled out the
 *  plain form-action flow — see startShellOAuth). The server action
 *  keeps its own shell branch as a no-JS fallback. */
export function OAuthForm({
  provider,
  action,
  verb,
  next,
  errorReturnPath,
  shell: shellProp,
}: {
  provider: OAuthProvider;
  action: OAuthFormAction;
  verb: string;
  next?: string;
  errorReturnPath: string;
  /** Server-detected shell flag, threaded from the page. Merged with client
   *  detection by useShellFlag — one shell fact, one source. */
  shell?: boolean;
}) {
  const [shellPending, setShellPending] = useState(false);
  const shell = useShellFlag(shellProp);

  // Warm the native chunk so the tap doesn't wait on a network fetch.
  // Shell-only, so web visitors never request it.
  useEffect(() => {
    if (shell) preloadNativeSignIn(provider);
  }, [shell, provider]);

  return (
    <form
      action={action}
      onSubmit={(event) => {
        if (!shell) return;
        event.preventDefault();
        if (shellPending) return;
        setShellPending(true);

        // Both params, like /auth/callback: `oauth_error` is the friendly
        // string, `oauth_error_code` is what the login page reads to show
        // tailored recovery guidance (#685). Sending only the first left the
        // native flow with strictly weaker recovery than the redirect flow.
        const failWith = (message: string, code?: string) => {
          setShellPending(false);
          const query = new URLSearchParams({ oauth_error: message });
          if (code) query.set("oauth_error_code", code);
          window.location.assign(`${withQuery(errorReturnPath)}${query}`);
        };

        // A provider with a native runner gets the system sheet: in-app,
        // Face ID, no app switch. Which providers have one is data, so a
        // second native provider is an entry in the registry rather than
        // another branch here. resolveShellSignIn owns the decision; what
        // stays here is the navigation, which genuinely belongs to the DOM.
        void (async () => {
          const outcome = resolveShellSignIn(
            await runNativeSignIn(provider),
            next,
          );
          if (outcome.kind === "navigate") {
            // Full navigation, not router.push: the session was written to
            // cookies client-side and every downstream gate reads it
            // server-side, so the next request must be a fresh one.
            window.location.assign(outcome.to);
            return;
          }
          if (outcome.kind === "idle") {
            setShellPending(false);
            return;
          }
          if (outcome.kind === "error") {
            failWith(outcome.message, outcome.code);
            return;
          }

          // Eject to the system browser; the webview stays here. Pending is
          // deliberately left set on success — it resets with the document
          // when the deep-link return (or the user) navigates. Capacitor
          // policy-cancels the off-host hop-0 navigation while the webview
          // stays parked with ShellBridge armed for the deep-link return.
          const redirect = await startShellOAuth(provider, next);
          if ("url" in redirect) window.location.assign(redirect.url);
          else failWith(redirect.error);
        })();
      }}
    >
      {next ? <input type="hidden" name="next" value={next} /> : null}
      <OAuthButton provider={provider} verb={verb} pending={shellPending} />
    </form>
  );
}
